import { Capacitor } from "@capacitor/core";
import type { NativeAuthClient } from "./types";

declare const __EPIC_MOBILE_DEV_ORIGIN__: string | undefined;
declare const __EPIC_NATIVE_GUEST_FIXTURE__: boolean | undefined;
declare const __EPIC_MOBILE_RELEASE_BUILD__: boolean | undefined;

// Preserve the separate WP2 development flow; the signed app does not expose it.
export const MOBILE_RELEASE_BUILD = typeof __EPIC_MOBILE_RELEASE_BUILD__ !== "undefined" && __EPIC_MOBILE_RELEASE_BUILD__ === true;
export const MOBILE_GUEST_ENABLED = !MOBILE_RELEASE_BUILD;

export const NATIVE_GUEST_FIXTURE = !MOBILE_RELEASE_BUILD && typeof __EPIC_NATIVE_GUEST_FIXTURE__ !== "undefined" && __EPIC_NATIVE_GUEST_FIXTURE__ === true;
export const EPIC_MOBILE_PUBLIC_ORIGIN = typeof __EPIC_MOBILE_RELEASE_BUILD__ !== "undefined" && __EPIC_MOBILE_RELEASE_BUILD__
  ? "https://ctek123.pro"
  : NATIVE_GUEST_FIXTURE ? "https://localhost:3443" : "https://ctek123.pro";
export const EPIC_NATIVE_API_PATH = "/mobile-api";
export const EPIC_NATIVE_AUTH_SCHEME = "pro.ctek123.epic";
export const EPIC_NATIVE_AUTH_HOST = "auth";
export const EPIC_NATIVE_AUTH_PATH = "/callback";
export const EPIC_NATIVE_AUTH_HTTPS_PATH = "/mobile/auth/callback";
export const EPIC_NATIVE_AUTH_DEV_HTTPS_PATH = "/mobile/auth/dev-callback";
export const EPIC_GOOGLE_WEB_SIGN_IN_PATH = "/login/?next=%2Fmobile%2F";

const NATIVE_AUTH_CLIENTS: Record<NativeAuthClient, { platform: "ios" | "android"; httpsPath: string }> = {
  "android-release": { platform: "android", httpsPath: EPIC_NATIVE_AUTH_HTTPS_PATH },
  "android-dev": { platform: "android", httpsPath: EPIC_NATIVE_AUTH_DEV_HTTPS_PATH },
  "ios-release": { platform: "ios", httpsPath: EPIC_NATIVE_AUTH_HTTPS_PATH }
};

function injectedDevelopmentOrigin() {
  if (MOBILE_RELEASE_BUILD) return null;
  if (NATIVE_GUEST_FIXTURE) return null;
  if (typeof __EPIC_MOBILE_DEV_ORIGIN__ === "undefined") return null;
  try {
    const target = new URL(__EPIC_MOBILE_DEV_ORIGIN__);
    const approved = target.protocol === "http:"
      && target.hostname === "localhost"
      && target.port === "3005"
      && target.pathname === "/"
      && !target.search
      && !target.hash;
    return approved ? target.origin : null;
  } catch (_) {
    return null;
  }
}

export function normalizeNativeAuthClient(
  value: string | null | undefined,
  platform: "ios" | "android"
): NativeAuthClient | null {
  const candidate = String(value || `${platform}-release`).trim().toLowerCase() as NativeAuthClient;
  return NATIVE_AUTH_CLIENTS[candidate]?.platform === platform ? candidate : null;
}

export function resolveMobileApiPrefix(platform = Capacitor.getPlatform()) {
  if (platform === "android") {
    return injectedDevelopmentOrigin() || `${EPIC_MOBILE_PUBLIC_ORIGIN}${EPIC_NATIVE_API_PATH}`;
  }
  return platform === "ios" ? `${EPIC_MOBILE_PUBLIC_ORIGIN}${EPIC_NATIVE_API_PATH}` : "/api";
}

export function resolveMobileSystemOrigin(platform = Capacitor.getPlatform()) {
  return platform === "android" ? injectedDevelopmentOrigin() || EPIC_MOBILE_PUBLIC_ORIGIN : EPIC_MOBILE_PUBLIC_ORIGIN;
}

export function resolveMobileRequestOrigin(
  platform = Capacitor.getPlatform(),
  origin = typeof window === "undefined" ? "" : window.location.origin
) {
  if (platform === "android" && origin === "https://localhost") return origin;
  if (platform === "ios" && origin === "capacitor://localhost") return origin;
  if (platform === "android" && injectedDevelopmentOrigin() && origin === "http://localhost:51732") return origin;
  return null;
}

export function resolveApprovedEpicUrl(path: string, approvedOrigin = EPIC_MOBILE_PUBLIC_ORIGIN) {
  try {
    const developmentOrigin = injectedDevelopmentOrigin();
    if (approvedOrigin !== EPIC_MOBILE_PUBLIC_ORIGIN && approvedOrigin !== developmentOrigin) return null;
    const base = new URL(approvedOrigin);
    const target = new URL(String(path || ""), base);
    return target.origin === base.origin ? target : null;
  } catch (_) {
    return null;
  }
}

export type GoogleSignInSurface = "native" | "web" | "unavailable";

export function resolveGoogleSignInSurface(
  platform = Capacitor.getPlatform(),
  currentHref = typeof window === "undefined" ? EPIC_MOBILE_PUBLIC_ORIGIN : window.location.href
): GoogleSignInSurface {
  if (platform === "android") return "native";
  if (platform === "ios") return "web";
  try {
    return new URL(currentHref).origin === new URL(EPIC_MOBILE_PUBLIC_ORIGIN).origin
      ? "web"
      : "unavailable";
  } catch (_) {
    return "unavailable";
  }
}

export function parseNativeAuthCallback(
  value: string,
  expectedPlatform?: "ios" | "android",
  expectedClient?: NativeAuthClient | null
) {
  try {
    const target = new URL(String(value || ""));
    const publicOrigin = new URL(EPIC_MOBILE_PUBLIC_ORIGIN);
    const httpsPath = target.pathname.replace(/\/$/, "");
    const isHttpsOrigin = target.origin === publicOrigin.origin;
    const isAppCallback = target.protocol === `${EPIC_NATIVE_AUTH_SCHEME}:`
      && target.hostname === EPIC_NATIVE_AUTH_HOST
      && target.pathname.replace(/\/$/, "") === EPIC_NATIVE_AUTH_PATH;
    if (!isHttpsOrigin && !isAppCallback) return null;

    const code = target.searchParams.get("code") || "";
    const platform = target.searchParams.get("platform");
    if (!/^[A-Za-z0-9_-]{32,128}$/.test(code)) return null;
    if (platform !== "ios" && platform !== "android") return null;
    if (expectedPlatform && platform !== expectedPlatform) return null;
    const client = normalizeNativeAuthClient(target.searchParams.get("client"), platform);
    if (!client || (expectedClient && client !== expectedClient)) return null;
    if (isHttpsOrigin && httpsPath !== NATIVE_AUTH_CLIENTS[client].httpsPath) return null;
    return { code, platform, client, source: isHttpsOrigin ? "https" as const : "app" as const };
  } catch (_) {
    return null;
  }
}
